/** Parse #rgb or #rrggbb (with optional leading #). */
export function parseHexColorRgb(hex: string): [number, number, number] | null {
  let h = hex.trim();
  if (!h) return null;
  if (h.startsWith("#")) h = h.slice(1);
  if (h.length === 3 && /^[0-9a-f]{3}$/i.test(h)) {
    h = `${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`;
  }
  if (h.length !== 6 || !/^[0-9a-f]{6}$/i.test(h)) return null;
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function relativeLuminance(rgb: [number, number, number]): number {
  const linear = rgb.map((c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
}

/**
 * True when white/light foreground has equal or better contrast than black
 * on this solid hex background (WCAG relative luminance).
 */
export function prefersLightForegroundOnHex(backgroundHex: string): boolean {
  const rgb = parseHexColorRgb(backgroundHex);
  if (!rgb) return false;
  const L = relativeLuminance(rgb);
  const contrastWithWhite = (1 + 0.05) / (L + 0.05);
  const contrastWithBlack = (L + 0.05) / 0.05;
  return contrastWithWhite > contrastWithBlack;
}

/** Stable 6-digit #rrggbb for <input type="color" /> when form value is missing or invalid. */
export const SECTION_BG_COLOR_PICKER_FALLBACK = "#f3f4f6";

export function hexForColorPickerInput(value: string): string {
  const t = value.trim();
  const rgb = parseHexColorRgb(t.startsWith("#") ? t : `#${t}`);
  if (rgb) {
    const toHex = (n: number) => n.toString(16).padStart(2, "0");
    return `#${toHex(rgb[0])}${toHex(rgb[1])}${toHex(rgb[2])}`.toLowerCase();
  }
  return SECTION_BG_COLOR_PICKER_FALLBACK;
}
